import { AlertTriangle, LogOut } from 'lucide-react'
import React, { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'

import { useChat } from '@/contexts/chat-context'

import { Conversation } from '@/types/chat.type'

interface LeaveConversationDialogProps {
  conversation: Conversation
  onClose: () => void
}

export const LeaveConversationDialog: React.FC<LeaveConversationDialogProps> = ({ conversation, onClose }) => {
  const { leaveConversation } = useChat()
  const [isLeaving, setIsLeaving] = useState(false)

  // Add null checks to prevent TypeError
  if (!conversation || !conversation.participants) {
    return null
  }

  const handleLeaveConversation = async () => {
    setIsLeaving(true)
    try {
      await leaveConversation(conversation.id)
      onClose()
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Failed to leave conversation:', error)
    } finally {
      setIsLeaving(false)
    }
  }

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-600">
            <LogOut className="h-5 w-5" />
            Leave Conversation
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          {/* Warning */}
          <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg">
            <AlertTriangle className="h-4 w-4 text-red-600" />
            <span className="text-sm text-red-800">
              You will no longer receive messages from this conversation and it will be removed from your chat list.
            </span>
          </div>

          {/* Conversation Info */}
          <div className="p-3 bg-gray-50 rounded-lg">
            <div className="text-sm text-gray-600 mb-2">Conversation:</div>
            <div className="font-medium">{conversation.name || `${conversation.participants.length} participants`}</div>
            {conversation.description && <div className="text-sm text-gray-500 mt-1">{conversation.description}</div>}
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleLeaveConversation} disabled={isLeaving} className="flex-1">
              {isLeaving ? 'Leaving...' : 'Leave Conversation'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
